import * as Sentry from '@sentry/react';
import { sendPasswordResetEmail, User } from 'firebase/auth';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  setDoc,
} from 'firebase/firestore';
import { auth, db, SUBSCRIPTIONS_PATH, USERS_PATH } from 'src/firebase';
import { DEFAULT_TRIAL_DAYS } from 'src/workbench/helpers/constants';
import { UserDoc } from 'src/workspace/workspace.interfaces';
import Stripe from 'stripe';

class UserDocService {
  userCollection = () => collection(db, USERS_PATH);
  userRef = (id: string) => doc(db, USERS_PATH, id);

  resetPassword = async (
    email: string,
    onSuccess: () => void,
    onError: (err: any) => void,
  ) => {
    try {
      if (!email) {
        throw new Error('No email provided');
      }
      await sendPasswordResetEmail(auth, email);
      onSuccess();
    } catch (e) {
      Sentry.captureException(e);
      onError(e);
    }
  };
  getUserDoc = async (id: string): Promise<UserDoc | null> => {
    const userDoc = await getDoc(doc(db, USERS_PATH, id));
    if (userDoc.exists()) {
      return userDoc.data() as UserDoc;
    } else {
      return null;
    }
  };
  getUserDocs = (config: { sort: string; sortAsc: boolean }) =>
    getDocs(
      query(
        collection(db, USERS_PATH),
        config.sort
          ? orderBy(config.sort, config.sortAsc ? 'asc' : 'desc')
          : orderBy('email'),
      ),
    );
  upsertUserDoc = async (id: string, data: Partial<UserDoc>) => {
    // in firestore we only should allow creation
    const userRef = doc(db, USERS_PATH, id);
    await setDoc(userRef, data, { merge: true });
  };

  createUser = async (user: User, termsVersion: number) => {
    const userRef = doc(db, USERS_PATH, user.uid);
    await setDoc(userRef, {
      email: user.email,
      createdAt: Date.now(),
      agreedToTerms: Date.now(),
      agreedToTermsVersion: termsVersion,
      trialDays: DEFAULT_TRIAL_DAYS,
    });
  };

  getUserSubscriptions = async (userId: string) => {
    try {
      const subscriptionsRef = collection(
        db,
        `${USERS_PATH}/${userId}/${SUBSCRIPTIONS_PATH}`,
      );
      const querySnapshot = await getDocs(subscriptionsRef);
      const subscriptions = querySnapshot.docs.map((doc) => doc.data());
      return subscriptions as Stripe.Subscription[];
    } catch (e) {
      console.error('Error getting subscriptions:', e);
      return [];
    }
  };
  hasActiveSub = (userDoc: UserDoc | null) => {
    return userDoc?.subscriptions?.some((sub) => sub.status === 'active');
  };
  getDaysLeft = (userDoc: UserDoc | null) => {
    if (!userDoc) {
      return 0;
    }
    const createdAt = userDoc.createdAt;
    const trialDays = userDoc.trialDays;
    if (!createdAt || !trialDays) {
      return 0;
    }
    const daysLeft = Math.ceil(
      (new Date(createdAt).getTime() +
        trialDays * 24 * 60 * 60 * 1000 -
        new Date().getTime()) /
        (1000 * 60 * 60 * 24),
    );
    return Math.max(0, daysLeft);
  };
}
export const userDocService = new UserDocService();
